import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CopyOutlined,
  FilePdfOutlined,
  LeftOutlined,
  MailOutlined,
  PictureOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  fetchDailyReportByDate,
  fetchDailyReportsByDateRange,
  updateDailyReport,
  generateDailyReportsPdfByDateRange,
  fetchDailyReportPhotos,
  notifyProjectUsersAboutDailyReport,
} from "../services/dailyReportsAPI";
import {
  Button,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Space,
  Typography,
  notification,
} from "antd";
import CrewTable from "../components/DailyReports/CrewTable";
import queryClient from "../queryClient";
import dayjs from "dayjs";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";
import { getTemperatureFieldLabel } from "../constants/countryAddress";
import VisitorsTable from "../components/DailyReports/VisitorsTable";
import DeliveriesTable from "../components/DailyReports/DeliveriesTable";
import MeetingsTable from "../components/DailyReports/MeetingsTable";
import NotesTable from "../components/DailyReports/NotesTable";
import QualityIssuesTable from "../components/DailyReports/QualityIssuesTable";
import SafetyIssuesTable from "../components/DailyReports/SafetyIssuesTable";
import CopyDailyReportModal from "../components/DailyReports/CopyDailyReportModal";
import AttachmentsSection from "../components/AttachmentsSection";
import DailyReportsPhotosModal from "../components/DailyReportsPhotosModal";
import NotifyUsersModal from "../components/NotifyUsersModal";
import "../components/DailyReports/dailyReport.css";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const VIEW_DAY = "day";
const VIEW_RANGE = "range";
/** Keep in sync with nicky-api MAX_DAILY_REPORT_RANGE_DAYS */
const MAX_RANGE_DAYS = 31;

const disableOutOfRangeSpan = (current, { from } = {}) => {
  if (!current || !from) return false;
  const earliest = from.add(-(MAX_RANGE_DAYS - 1), "day");
  const latest = from.add(MAX_RANGE_DAYS - 1, "day");
  return current.isBefore(earliest, "day") || current.isAfter(latest, "day");
};

const initalDailyReport = {
  site_hours_from: "7:00 AM",
  site_hours_to: "3:30 PM",
  temperature: null,
  attachments: [],
};

const conditions = [
  { label: "Sunny", value: "Sunny" },
  { label: "Cloudy", value: "Cloudy" },
  { label: "Windy", value: "Windy" },
  { label: "Rainy", value: "Rainy" },
  { label: "Snowy", value: "Snowy" },
  { label: "Stormy", value: "Stormy" },
];

const DailyReports = () => {
  const [viewMode, setViewMode] = useState(VIEW_DAY);
  const [date, setDate] = useState(dayjs(new Date()));
  const [dateRange, setDateRange] = useState([dayjs(new Date()), dayjs(new Date())]);
  const [localDailyReport, setLocalDailyReport] = useState(initalDailyReport);
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false);
  const [isPhotosOpen, setIsPhotosOpen] = useState(false);
  const [photosDate, setPhotosDate] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [photosPage, setPhotosPage] = useState(1);
  const [photosPageSize] = useState(8);
  const [photosTotal, setPhotosTotal] = useState(0);
  const [isNotifyUsersModalVisible, setIsNotifyUsersModalVisible] =
    useState(false);
  const [notifyRange, setNotifyRange] = useState(null);
  const [isNotifying, setIsNotifying] = useState(false);
  const { selectedProject, currentUser, setSelectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const [searchParams, setSearchParams] = useSearchParams();
  const dateFromUrl = searchParams.get("date");
  const projectIdFromUrl = searchParams.get("project_id");

  const isListMode = viewMode === VIEW_RANGE;

  useEffect(() => {
    if (!projectIdFromUrl || !currentUser?.projects?.length) return;
    if (selectedProject?.project_id === projectIdFromUrl) return;
    const match = currentUser.projects.find(
      (p) => p.project_id === projectIdFromUrl,
    );
    if (match) {
      setSelectedProject(match);
    }
  }, [
    projectIdFromUrl,
    currentUser?.projects,
    selectedProject?.project_id,
    setSelectedProject,
  ]);

  useEffect(() => {
    if (!dateFromUrl) return;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateFromUrl) && dayjs(dateFromUrl).isValid()) {
      setViewMode(VIEW_DAY);
      setDate(dayjs(dateFromUrl));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("date");
    next.delete("project_id");
    setSearchParams(next, { replace: true });
  }, [dateFromUrl, searchParams, setSearchParams]);

  const pdfRange = useMemo(() => {
    if (isListMode) return dateRange;
    return [date, date];
  }, [isListMode, dateRange, date]);

  const {
    data: dailyReport,
    refetch: refetchDailyReport,
    isLoading: isDailyReportLoading,
  } = useQuery({
    queryKey: ["daily-report", projectId, date?.format?.("YYYY-MM-DD")],
    queryFn: () => fetchDailyReportByDate(projectId, date),
    enabled: !!projectId && !!date && !isListMode,
  });

  const { data: rangeReports = [] } = useQuery({
    queryKey: [
      "daily-reports-range",
      projectId,
      dateRange?.[0]?.format?.("YYYY-MM-DD"),
      dateRange?.[1]?.format?.("YYYY-MM-DD"),
    ],
    queryFn: () =>
      fetchDailyReportsByDateRange({
        projectId,
        startDate: dateRange[0],
        endDate: dateRange[1],
      }),
    enabled: !!projectId && isListMode && !!dateRange?.[0] && !!dateRange?.[1],
  });

  const { mutate: updateDailyReportMutation } = useMutation({
    mutationFn: updateDailyReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["daily-report"] });
      queryClient.invalidateQueries({ queryKey: ["daily-reports-range"] });
      notification.success({ message: "Daily report updated" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update daily report",
        description: error.message,
      });
    },
  });

  const { mutate: generatePdfMutation, isPending: isGeneratingPdf } =
    useMutation({
      mutationFn: async ({ startDate, endDate, previewTab }) => {
        try {
          const fileUrl = await generateDailyReportsPdfByDateRange({
            projectId,
            startDate,
            endDate,
          });
          if (!fileUrl) {
            throw new Error("PDF URL is missing");
          }
          // Tab is opened on click (user gesture) so popup blockers don't strip it.
          if (previewTab && !previewTab.closed) {
            previewTab.location.href = fileUrl;
          } else {
            window.open(fileUrl, "_blank", "noopener,noreferrer");
          }
          return fileUrl;
        } catch (error) {
          if (previewTab && !previewTab.closed) {
            previewTab.close();
          }
          throw error;
        }
      },
      onSuccess: () => {
        notification.success({ message: "PDF opened in a new tab" });
      },
      onError: (error) => {
        notification.error({
          message: "Failed to generate PDF",
          description: error.message,
        });
      },
    });

  useEffect(() => {
    if (dailyReport && !isListMode) {
      setLocalDailyReport(dailyReport);
    }
  }, [dailyReport, isListMode]);

  useEffect(() => {
    if (!isListMode) {
      refetchDailyReport();
    }
  }, [selectedProject, isListMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setLocalDailyReport((prevFormData) => ({
      ...prevFormData,
      [name]: type === "checkbox" ? checked : value,
    }));

    updateDailyReportMutation({
      dailyReport: { [name]: value },
      dailyReportId: localDailyReport._id,
    });
  };

  const handleDirectChange = (value, name) => {
    setLocalDailyReport((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    updateDailyReportMutation({
      dailyReport: { [name]: value },
      dailyReportId: localDailyReport._id,
    });
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === VIEW_DAY) {
      const nextDate = dateRange?.[0] || date || dayjs();
      setDate(nextDate);
      setDateRange([nextDate, nextDate]);
    } else {
      const start = date || dayjs();
      setDateRange([start, start.add(6, "day")]);
    }
  };

  const handleDateChange = (nextDate) => {
    if (!nextDate) return;
    setDate(nextDate);
    setDateRange([nextDate, nextDate]);
  };

  const shiftDay = (delta) => {
    const nextDate = (date || dayjs()).add(delta, "day");
    handleDateChange(nextDate);
  };

  const handleRangeChange = (range) => {
    if (!range?.[0] || !range?.[1]) return;
    const spanDays = range[1].diff(range[0], "day") + 1;
    if (spanDays > MAX_RANGE_DAYS) {
      notification.error({
        message: "Date range too large",
        description: `Select up to ${MAX_RANGE_DAYS} days.`,
      });
      return;
    }
    setDateRange(range);
    if (range[0].isSame(range[1], "day")) {
      setDate(range[0]);
    }
  };

  const handleAttachmentsChange = (attachments) => {
    setLocalDailyReport((prevFormData) => ({
      ...prevFormData,
      attachments,
    }));

    updateDailyReportMutation({
      dailyReport: { attachments },
      dailyReportId: localDailyReport._id,
    });
  };

  const handleGeneratePdf = () => {
    if (!pdfRange?.[0] || !pdfRange?.[1]) {
      notification.error({
        message: "Select a date",
        description: "Choose a day or date range before generating the PDF.",
      });
      return;
    }
    if (!isListMode) {
      if (isDailyReportLoading) {
        return;
      }
      if (!dailyReport?._id) {
        notification.error({
          message: "Daily report not ready",
          description: "Wait for the day to load before generating the PDF.",
        });
        return;
      }
    }
    // Open immediately on the click gesture, then navigate when the URL is ready.
    const previewTab = window.open("about:blank", "_blank");
    generatePdfMutation({
      startDate: pdfRange[0],
      endDate: pdfRange[1],
      previewTab,
    });
  };

  const handleNotifyUsers = () => {
    if (!pdfRange?.[0] || !pdfRange?.[1]) {
      notification.error({
        message: "Select a date",
        description: "Choose a day or date range before emailing the daily report.",
      });
      return;
    }
    if (!isListMode) {
      if (isDailyReportLoading) {
        return;
      }
      if (!dailyReport?._id) {
        notification.error({
          message: "Daily report not ready",
          description: "Wait for the day to load before emailing the daily report.",
        });
        return;
      }
    }
    setNotifyRange([pdfRange[0], pdfRange[1]]);
    setIsNotifyUsersModalVisible(true);
  };

  const handleSendNotification = async (userIds, note) => {
    if (!notifyRange) return;

    setIsNotifying(true);
    setIsNotifyUsersModalVisible(false);

    try {
      const result = await notifyProjectUsersAboutDailyReport({
        projectId,
        startDate: notifyRange[0],
        endDate: notifyRange[1],
        userIds,
        note,
      });
      if (!result?.recipients_count) {
        notification.warning({
          message: "Notification not sent",
          description: result?.message || "No emails were sent.",
        });
        return;
      }
      notification.success({
        message: "Notification sent successfully",
        description: `Email sent to ${result.recipients_count} project user${result.recipients_count !== 1 ? "s" : ""}`,
      });
    } catch (error) {
      let errorMessage = "An unknown error occurred";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          try {
            const errorData = JSON.parse(error.response.data);
            errorMessage =
              errorData?.detail?.message || errorData?.detail || errorMessage;
          } catch {
            errorMessage = error.response.data;
          }
        } else {
          errorMessage =
            error.response.data?.detail?.message ||
            error.response.data?.detail ||
            errorMessage;
        }
      }

      notification.error({
        message: "Failed to send notification",
        description: errorMessage,
      });
    } finally {
      setIsNotifying(false);
      setNotifyRange(null);
    }
  };

  const notifyItemName = useMemo(() => {
    if (!notifyRange?.[0] || !notifyRange?.[1]) return "Daily Report";
    const [start, end] = notifyRange;
    if (start.isSame(end, "day")) {
      return `Daily Report ${start.format(DATE_PICKER_FORMAT)}`;
    }
    return `Daily Reports ${start.format(DATE_PICKER_FORMAT)} – ${end.format(DATE_PICKER_FORMAT)}`;
  }, [notifyRange]);

  const openPhotos = async () => {
    setIsPhotosOpen(true);
    await loadPhotos({ d: photosDate, p: 1 });
  };

  const loadPhotos = async ({ d, p = photosPage }) => {
    try {
      setIsLoadingPhotos(true);
      const data = await fetchDailyReportPhotos({
        projectId,
        date: d || undefined,
        page: p,
        pageSize: photosPageSize,
      });
      setPhotos(Array.isArray(data?.data) ? data.data : []);
      setPhotosTotal(data?.total || 0);
      setPhotosPage(p);
    } catch (e) {
      notification.error({ message: "Failed to load photos" });
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  const tableProps = {
    dailyReport: localDailyReport,
    updateDailyReportMutation,
    showDateColumn: true,
    rangeReports: isListMode ? rangeReports : null,
  };

  const rangeAttachments = useMemo(() => {
    if (!isListMode || !Array.isArray(rangeReports)) return [];
    return rangeReports.flatMap((report) =>
      (report.attachments || []).map((attachment, index) => ({
        ...attachment,
        _id: attachment._id || `${report._id}-att-${index}`,
        date: report.date,
      })),
    );
  }, [isListMode, rangeReports]);

  const isDayPdfBlocked =
    !isListMode && (isDailyReportLoading || !dailyReport?._id);

  return (
    <div className="daily-report-page">
      <div className="daily-report-header">
        <h1 className="daily-report-title">Daily Report</h1>

        <div className="daily-report-toolbar">
          <div className="daily-report-toolbar-left">
            <Segmented
              className="daily-report-mode-toggle"
              size="large"
              value={viewMode}
              onChange={handleViewModeChange}
              options={[
                { label: "Day", value: VIEW_DAY },
                { label: "Range", value: VIEW_RANGE },
              ]}
            />

            {viewMode === VIEW_DAY ? (
              <Space.Compact className="daily-report-date-control" size="large">
                <Button
                  icon={<LeftOutlined />}
                  onClick={() => shiftDay(-1)}
                  aria-label="Previous day"
                />
                <DatePicker
                  className="daily-report-datepicker"
                  format={DATE_PICKER_FORMAT}
                  onChange={handleDateChange}
                  value={date}
                  allowClear={false}
                  inputReadOnly
                />
                <Button
                  icon={<RightOutlined />}
                  onClick={() => shiftDay(1)}
                  aria-label="Next day"
                />
              </Space.Compact>
            ) : (
              <RangePicker
                className="daily-report-rangepicker"
                size="large"
                format={DATE_PICKER_FORMAT}
                value={dateRange}
                onChange={handleRangeChange}
                allowClear={false}
                disabledDate={disableOutOfRangeSpan}
              />
            )}

            {viewMode === VIEW_DAY && (
              <Button
                size="large"
                icon={<CopyOutlined />}
                onClick={() => setIsCopyModalVisible(true)}
              >
                Copy From
              </Button>
            )}
          </div>

          <div className="daily-report-toolbar-right">
            <Button
              size="large"
              icon={<PictureOutlined />}
              onClick={openPhotos}
            >
              All Photos
            </Button>
            <Button
              size="large"
              icon={<MailOutlined />}
              onClick={handleNotifyUsers}
              loading={isNotifying}
              disabled={isDayPdfBlocked || isNotifying}
            >
              {isNotifying ? "Sending..." : "Email"}
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<FilePdfOutlined />}
              onClick={handleGeneratePdf}
              loading={isGeneratingPdf}
              disabled={isDayPdfBlocked || isGeneratingPdf}
            >
              {isGeneratingPdf ? "Generating..." : "Generate PDF"}
            </Button>
          </div>
        </div>

        {isListMode && (
          <Text type="secondary" className="daily-report-range-hint">
            List view · {dateRange[0].format(DATE_PICKER_FORMAT)} –{" "}
            {dateRange[1].format(DATE_PICKER_FORMAT)} (max {MAX_RANGE_DAYS}{" "}
            days). Switch to Day to edit site hours or add attachments.
          </Text>
        )}
      </div>

      {!isListMode && (
        <Row gutter={16} className="daily-report-meta">
          <Col xs={24} sm={12} md={6}>
            <Text className="daily-report-field-label">Site Hours From</Text>
            <Input
              name="site_hours_from"
              value={localDailyReport?.site_hours_from}
              placeholder="7:00 AM"
              onChange={handleChange}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text className="daily-report-field-label">Site Hours To</Text>
            <Input
              name="site_hours_to"
              value={localDailyReport?.site_hours_to}
              placeholder="3:30 PM"
              onChange={handleChange}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text className="daily-report-field-label">
              {getTemperatureFieldLabel(selectedProject?.country_code)}
            </Text>
            <InputNumber
              className="w-full"
              name="temperature"
              value={localDailyReport?.temperature}
              placeholder="Temperature"
              onChange={(value) => handleDirectChange(value, "temperature")}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text className="daily-report-field-label">Condition</Text>
            <Select
              placeholder="Condition"
              value={localDailyReport?.conditions}
              name="conditions"
              className="w-full"
              options={conditions}
              onChange={(value) => handleDirectChange(value, "conditions")}
            />
          </Col>
        </Row>
      )}

      <div className="mt-4">
        <CrewTable {...tableProps} />
        <Title className="mt-6" level={3}>
          Attachments
        </Title>
        {isListMode ? (
          rangeAttachments.length > 0 ? (
            <AttachmentsSection
              projectId={projectId}
              value={rangeAttachments}
              onChange={() => {}}
              disabled
              accept=".jpg,.jpeg,.png,.heic,.pdf"
            />
          ) : (
            <Text type="secondary">No attachments in this date range.</Text>
          )
        ) : (
          <AttachmentsSection
            projectId={projectId}
            value={localDailyReport?.attachments || []}
            onChange={handleAttachmentsChange}
            label="Add Attachments"
            title="Upload Attachments"
            description="Upload your attachments here."
            accept=".jpg,.jpeg,.png,.heic,.pdf"
          />
        )}
        <VisitorsTable {...tableProps} />
        <DeliveriesTable {...tableProps} />
        <MeetingsTable {...tableProps} />
        <QualityIssuesTable {...tableProps} />
        <SafetyIssuesTable {...tableProps} />
        <NotesTable {...tableProps} />
      </div>
      <CopyDailyReportModal
        visible={isCopyModalVisible}
        onClose={() => setIsCopyModalVisible(false)}
        copyToDate={date}
        projectId={projectId}
      />
      <NotifyUsersModal
        visible={isNotifyUsersModalVisible}
        onClose={() => {
          setIsNotifyUsersModalVisible(false);
          setNotifyRange(null);
        }}
        itemName={notifyItemName}
        onNotify={handleSendNotification}
        showNote
      />
      <DailyReportsPhotosModal
        open={isPhotosOpen}
        onClose={() => setIsPhotosOpen(false)}
        photos={photos}
        loading={isLoadingPhotos}
        onSearchDate={(d) => {
          setPhotosDate(d);
          loadPhotos({ d, p: 1 });
        }}
        searchDate={photosDate}
        pagination={{
          page: photosPage,
          pageSize: photosPageSize,
          total: photosTotal,
        }}
        onPageChange={(p) => loadPhotos({ d: photosDate, p })}
      />
    </div>
  );
};

export default DailyReports;
